<template>
  <el-dialog
    :title="$t('components.ScoreDialog.9398853-0')"
    :visible.sync="dialogVisible"
    width="60%"
    top="5vh"
    @open="handleOpen"
    @closed="handleClosed"
    :close-on-click-modal="false"
    :modal-append-to-body="true"
    :append-to-body="true"
    custom-class="score-dialog"
  >
    <div v-loading="loading" :element-loading-text="$t('components.ScoreDialog.9398853-1')">
      <div class="machine-info">
        <span>{{ $t("components.ScoreDialog.9398853-2") }} {{ machineInfo.equipmentNo }}</span>
        <span style="margin-left: 30px"
          >{{ $t("components.ScoreDialog.9398853-3") }} {{ machineInfo.equipmentName }}</span
        >
      </div>
      <div class="overall-rating">
        <span>{{ $t("components.ScoreDialog.9398853-4") }} </span>
        <el-rate
          v-model="overallScore"
          disabled
          show-score
          text-color="#ff9900"
          :score-template="() => value + $t('components.ScoreDialog.9398853-5')"
        >
        </el-rate>
        <span style="margin-left: 10px">({{ totalReviews }} {{ $t("components.ScoreDialog.9398853-6") }})</span>
      </div>

      <el-tabs v-model="activeTab" @tab-click="handleTabClick">
        <el-tab-pane v-for="tab in ratingTabs" :key="tab.name" :label="`${tab.label} (${tab.count})`" :name="tab.name">
          <el-table
            :data="ratingList"
            :empty-text="$t('components.ScoreDialog.9398853-7')"
            style="width: 100%; margin-top: 10px"
            max-height="400"
            border
          >
            <el-table-column
              prop="nickName"
              :label="$t('components.ScoreDialog.9398853-8')"
              width="150"
            ></el-table-column>
            <el-table-column
              prop="userName"
              :label="$t('components.ScoreDialog.9398853-9')"
              width="180"
            ></el-table-column>
            <el-table-column
              prop="content"
              :label="$t('components.ScoreDialog.9398853-10')"
              :show-overflow-tooltip="true"
            ></el-table-column>
            <el-table-column prop="rating" :label="$t('components.ScoreDialog.9398853-11')" width="80">
              <template slot-scope="scope">
                {{ scope.row.rating }} {{ $t("components.ScoreDialog.9398853-12") }}
              </template>
            </el-table-column>
            <el-table-column
              prop="createTime"
              :label="$t('components.ScoreDialog.9398853-13')"
              width="180"
            ></el-table-column>
          </el-table>

          <el-pagination
            style="margin-top: 20px; text-align: right"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="currentPage"
            :page-sizes="[10, 20, 50]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="tableTotal"
          ></el-pagination>
        </el-tab-pane>
      </el-tabs>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="closeDialog" size="small">{{ $t("components.ScoreDialog.9398853-14") }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
  export default {
    name: "ScoreDialog",
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      machineInfo: {
        type: Object,
        required: true,
        default: () => ({ equipmentNo: "", equipmentName: "", id: null }) // Provide default structure
      }
    },
    data() {
      return {
        loading: false,
        dialogVisible: this.visible,
        overallScore: 0, // Overall average score
        totalReviews: 0, // Total number of reviews
        activeTab: "5", // Default to 5 stars
        ratingTabs: [
          // Initialize with default structure, counts will be fetched
          { name: "5", label: this.$t("components.ScoreDialog.9398853-15"), count: 0 },
          { name: "4", label: this.$t("components.ScoreDialog.9398853-16"), count: 0 },
          { name: "3", label: this.$t("components.ScoreDialog.9398853-17"), count: 0 },
          { name: "2", label: this.$t("components.ScoreDialog.9398853-18"), count: 0 },
          { name: "1", label: this.$t("components.ScoreDialog.9398853-19"), count: 0 }
        ],
        ratingList: [], // Data for the table
        currentPage: 1,
        pageSize: 10,
        tableTotal: 0 // Total items for the current tab's table
      };
    },
    watch: {
      visible(newVal) {
        this.dialogVisible = newVal;
      },
      dialogVisible(newVal) {
        this.$emit("update:visible", newVal); // Notify parent about visibility change
      }
    },
    methods: {
      handleOpen() {
        this.resetData();
        this.fetchScoreData(); // Renamed and call the combined fetch method
      },
      handleClosed() {
        this.resetData();
      },
      resetData() {
        this.loading = false;
        this.overallScore = 0;
        this.totalReviews = 0;
        this.activeTab = "5";
        this.ratingTabs = this.ratingTabs.map(tab => ({ ...tab, count: 0 })); // Reset counts
        this.ratingList = [];
        this.currentPage = 1;
        this.pageSize = 10;
        this.tableTotal = 0;
      },
      closeDialog() {
        this.dialogVisible = false;
      },
      fetchScoreData() {
        // Renamed from fetchRatings
        if (!this.machineInfo || !this.machineInfo.equipmentNo) return;
        this.loading = true;
        const params = {
          equipmentNo: this.machineInfo.equipmentNo,
          starCounts: parseInt(this.activeTab), // Corrected parameter name: score -> starCounts
          pageIndex: this.currentPage, // Corrected parameter name: page -> pageIndex
          pageSize: this.pageSize // Corrected parameter name: size -> pageSize
        };

        this.$api
          .request(this.$url.Survey.score, this.$method.get, params)
          .then(res => {
            if (res.code === 0 && res.data) {
              // Update table data
              this.ratingList = res.data.records || [];
              this.tableTotal = parseInt(res.data.total || 0);

              // Update summary data (only needs to be done once, ideally)
              // We fetch it on every page change here, might optimize later if needed
              if (res.data.sum) {
                this.overallScore = parseFloat(res.data.sum.avg || 0);
                this.totalReviews = parseInt(res.data.sum.num || 0);
                // Update tab counts using the starCounts object from the sum
                this.ratingTabs = this.ratingTabs.map(tab => ({
                  ...tab,
                  count: parseInt(res.data.sum.starCounts?.[tab.name] || 0)
                }));
              }
            } else {
              this.ratingList = [];
              this.tableTotal = 0;
              // Reset summary on error too
              this.overallScore = 0;
              this.totalReviews = 0;
              this.ratingTabs = this.ratingTabs.map(tab => ({ ...tab, count: 0 }));
              this.$message.error(res.msg || this.$t("components.ScoreDialog.9398853-20"));
            }
            this.loading = false;
          })
          .catch(() => {
            this.ratingList = [];
            this.tableTotal = 0;
            this.overallScore = 0;
            this.totalReviews = 0;
            this.ratingTabs = this.ratingTabs.map(tab => ({ ...tab, count: 0 }));
            this.$message.error(this.$t("components.ScoreDialog.9398853-21"));
            this.loading = false;
          });
      },
      handleTabClick() {
        this.currentPage = 1;
        this.tableTotal = 0;
        this.ratingList = [];
        this.fetchScoreData(); // Call the combined fetch method
      },
      handleSizeChange(val) {
        this.pageSize = val;
        this.currentPage = 1;
        this.fetchScoreData(); // Call the combined fetch method
      },
      handleCurrentChange(val) {
        this.currentPage = val;
        this.fetchScoreData(); // Call the combined fetch method
      }
    }
  };
</script>

<style lang="scss" scoped>
  .score-dialog {
    .machine-info {
      font-size: 14px;
      color: #606266;
      margin-bottom: 15px;
    }
    .overall-rating {
      display: flex;
      align-items: center;
      margin-bottom: 20px;
      font-size: 14px;
      color: #606266;

      .el-rate {
        margin-left: 5px;
        margin-right: 5px;
        height: auto; // Override default height if needed
        line-height: 1; // Adjust line height for better alignment
        display: inline-flex; // Helps alignment
        vertical-align: middle; // Align vertically
      }
      // Adjust el-rate visual alignment if needed
      ::v-deep .el-rate__icon {
        vertical-align: middle;
      }
      ::v-deep .el-rate__text {
        vertical-align: middle;
        margin-left: 5px;
      }
    }
    // Style tabs if needed
    // ::v-deep .el-tabs__header {
    //   margin-bottom: 15px;
    // }
  }
  .dialog-footer {
    text-align: right;
  }
</style>
